// BL-177 Step 2n: the "⋯" menu that holds a conversation's extra actions on phones (Note, Flag, Create Reservation, Archive, People...).
// An accessible menu button: the trigger is a 44 px 3D pill with aria-haspopup/aria-expanded; the panel opens under it, Escape or a tap
// outside closes it, arrow keys move between items, and focus returns to the trigger. The items stay MOUNTED while the menu is closed
// (hidden), because some of them own a flow with its own state (Create Reservation keeps its review tile and notice).
import React, { useCallback, useEffect, useId, useRef, useState, Children } from 'react';
import { actionPillCssVars } from '../ui/actionPillTokens';
import { MENU_CLOSE_DWELL_MS } from '../../lib/uiMotion';

export default function ThreadActionsMenu({ label = 'More conversation actions', children }) {
    const [open, setOpen] = useState(false);
    const [closing, setClosing] = useState(false);
    const closeTimer = useRef(null);
    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const focusOnOpen = useRef(false);
    // Comms polish hotfix: hover-opened (mouse). A click on the trigger then pins it open instead of toggling it shut.
    const hoverOpened = useRef(false);
    const panelId = `ia-thread-actions-${useId().replace(/:/g, '')}`;

    const close = useCallback((returnFocus = true) => {
        hoverOpened.current = false;
        setClosing(true);
        setOpen(false);
        if (returnFocus) triggerRef.current?.focus();
    }, []);

    useEffect(() => { if (!closing) return; const timer = setTimeout(() => setClosing(false),100); return () => clearTimeout(timer); }, [closing]);
    useEffect(() => () => clearTimeout(closeTimer.current), []);
    useEffect(() => {
        if (!open) return undefined;
        const items = () => Array.from(panelRef.current?.querySelectorAll('button:not(:disabled), a[href]') || []);
        if (focusOnOpen.current) { items()[0]?.focus(); focusOnOpen.current = false; }
        const onPointer = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) close(false);
        };
        const onKey = (event) => {
            if (event.key === 'Escape') { event.stopPropagation(); close(); return; }
            if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Tab'].includes(event.key)) return;
            if (event.key === 'Tab') { close(false); return; }
            const list = items();
            if (!list.length) return;
            event.preventDefault();
            const at = list.indexOf(document.activeElement);
            const next = event.key === 'Home' ? 0 : event.key === 'End' ? list.length - 1 : event.key === 'ArrowDown' ? (at + 1) % list.length : (at - 1 + list.length) % list.length;
            list[next].focus();
        };
        document.addEventListener('pointerdown', onPointer);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('pointerdown', onPointer);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, close]);

    return (
        <div ref={wrapperRef} className="ia-thread-actions" data-thread-actions="true" onMouseEnter={() => {
                clearTimeout(closeTimer.current);
                if (navigator.maxTouchPoints || open) return;
                hoverOpened.current = true;
                setOpen(true);
            }}
            onMouseLeave={() => {
                if (!navigator.maxTouchPoints && open) closeTimer.current = setTimeout(() => close(false), MENU_CLOSE_DWELL_MS);
            }}>
            <button
                ref={triggerRef}
                type="button"
                className="ia-btn3d ia-comms-pill ia-thread-actions__trigger"
                aria-label={label}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={panelId}
                onKeyDown={event => { if (['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); focusOnOpen.current = true; setOpen(true); } }}
                onClick={() => {
                    if (open && hoverOpened.current) { hoverOpened.current = false; return; }
                    focusOnOpen.current = true;
                    hoverOpened.current = false;
                    setOpen((current) => !current);
                }}
                data-thread-actions-trigger="true"
            >
                <span aria-hidden="true">⋯</span>
            </button>
            {/* a tap on any item closes the menu after the item has run */}
            <div
                ref={panelRef}
                id={panelId}
                role="menu"
                style={actionPillCssVars()}
                aria-label={label}
                hidden={!open && !closing}
                data-open={open ? 'true' : 'false'}
                aria-hidden={!open ? 'true' : undefined}
                className="ia-thread-actions__panel"
                onClick={(event) => { if (event.target.closest('button, a[href]') && !event.target.closest('[data-keep-menu-open]')) setOpen(false); }}
            >
                {Children.map(children, (child,index) => child && <div style={{'--menu-index':index}}>{child}</div>)}
            </div>
        </div>
    );
}
